/**
 * Switch -- Renders only the first matching Route child.
 *
 * Iterates over Route children and renders the first one whose path
 * matches the current hash location. Non-Route children are skipped.
 */
import React, { Children, isValidElement } from "react";
import { useRouterContext, RouterContext } from "./Router";
import { matchRoute } from "./matchRoute";

export function Switch({ children }: { children: React.ReactNode }) {
  const ctx = useRouterContext();

  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;

    // Extract the path prop from Route-like children
    const props = child.props as {
      path?: string;
      component?: React.ComponentType<Record<string, string>>;
      children?:
        | React.ReactNode
        | ((params: Record<string, string>) => React.ReactNode);
    };

    if (typeof props.path !== "string") continue;

    const params = matchRoute(props.path, ctx.path);
    if (params === null) continue;

    const childCtx = { ...ctx, params };

    if (props.component) {
      const Component = props.component;
      return (
        <RouterContext.Provider value={childCtx}>
          <Component {...params} />
        </RouterContext.Provider>
      );
    }

    if (typeof props.children === "function") {
      return (
        <RouterContext.Provider value={childCtx}>
          {props.children(params)}
        </RouterContext.Provider>
      );
    }

    return (
      <RouterContext.Provider value={childCtx}>
        {props.children}
      </RouterContext.Provider>
    );
  }

  return null;
}
